import {
  Notification,
  UserNotificationSettings,
} from "../interfaces/notifications.types";
import { getTimeElapsed } from "../../utils/notifications/getTimeElapsed";
import api from "../../api";

export const getNotifications = async (): Promise<Notification[]> => {
  try {
    const { data } = await api.get("/notifications/");
    // Make sure we handle both paginated and non-paginated responses
    const notifications = data.results || data;

    return notifications
      .sort(
        (a: Notification, b: Notification) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .map((notification: Notification) => ({
        ...notification,
        timeSince: getTimeElapsed(notification.created_at),
      }));
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};

export const getNotificationById = async (
  notificationId: string,
): Promise<Notification | null> => {
  try {
    const { data } = await api.get(`/notifications/${notificationId}/`);
    return {
      ...data,
      timeSince: getTimeElapsed(data.created_at),
    };
  } catch (error) {
    console.error(`Error fetching notification ${notificationId}:`, error);
    return null;
  }
};

export const markNotificationAsRead = async (
  notificationId: string,
): Promise<boolean> => {
  try {
    const { data } = await api.patch(`/notifications/${notificationId}`, {
      read: true,
    });
    return data.read;
  } catch (error) {
    console.error(
      `Error marking notification ${notificationId} as read:`,
      error,
    );
    return false;
  }
};

export const markAllNotificationsAsRead = async (
  notificationIds: string[],
): Promise<boolean> => {
  try {
    // Since the endpoint doesn't exist yet, we'll mark them one by one
    const promises = notificationIds.map((id) => markNotificationAsRead(id));
    const results = await Promise.all(promises);
    return results.every((result) => result === true);
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    return false;
  }
};

export const deleteNotification = async (
  notificationId: string,
): Promise<boolean> => {
  try {
    await api.delete(`/notifications/${notificationId}`);
    return true;
  } catch (error) {
    console.error(`Error deleting notification ${notificationId}:`, error);
    return false;
  }
};

export const retrieveNotificationSettings =
  async (): Promise<UserNotificationSettings | null> => {
    try {
      const { data } = await api.get("/notifications/settings/");
      return data;
    } catch (error) {
      console.error("Error fetching notification settings:", error);
      return null;
    }
  };

export const updateNotificationSettings = async (
  settings: Partial<UserNotificationSettings>,
): Promise<UserNotificationSettings | null> => {
  try {
    const { data } = await api.patch("/notifications/settings/", settings);
    return data;
  } catch (error) {
    console.error("Error updating notification settings:", error);
    return null;
  }
};

// We'll implement this if the backend endpoint is added
// export const getUnreadCount = async (): Promise<number> => {
//   try {
//     const { data } = await api.get('/notifications/unread-count/');
//     return data.count;
//   } catch (error) {
//     console.error("Error fetching unread count:", error);
//     return 0;
//   }
// };
